import {
  TopicClient,
  CacheClient,
  Configurations,
  CredentialProvider,
  TopicPublish,
  TopicSubscribe,
  type TopicItem,
} from "@gomomento/sdk-web";
import { apiClient, type PresenceStatus } from "@slumbr/core";

interface MomentoGrant {
  user: string;
  notifications: string;
  ownPresence: string;
  conversations: string[];
  channels: string[];
  presenceSubscribes: string[];
}

interface RealtimeTokenResponse {
  /** Disposable Momento token (NOT raw API key). */
  apiKey: string;
  endpoint: string;
  /** Epoch ms when this disposable token expires. */
  expiresAt?: number;
  /** Separate item-scoped disposable token for direct presence cache writes. */
  cacheApiKey?: string;
  cacheEndpoint?: string;
  cacheExpiresAt?: number;
  cacheName: string;
  topicPrefix?: string;
  topics: string[];
  grant?: MomentoGrant;
}

type MomentoToken = RealtimeTokenResponse & { fetchedAt: number };

const tokenCache = new Map<string, MomentoToken>();
const tokenPromises = new Map<string, Promise<MomentoToken>>();
const tokenBackoff = new Map<string, { until: number; delayMs: number }>();
// Keyed by token: the app-level token and per-conversation scoped tokens are
// used side by side (e.g. typing publish vs. presence), so a single-slot
// cache would rebuild the client on every alternation.
const topicClientCache = new Map<string, TopicClient>();
const TOPIC_CLIENT_CACHE_MAX = 8;
let cacheClientCache: { token: string; client: CacheClient } | null = null;

export interface TokenRequestParams {
  conversationIds?: string[];
  channelIds?: string[];
  presenceUserIds?: string[];
}

function shouldRefresh(token: MomentoToken | null): boolean {
  if (!token) return true;
  if (token.expiresAt) {
    // Refresh 5 minutes before expiry to avoid mid-stream invalidation.
    return Date.now() > token.expiresAt - 5 * 60 * 1000;
  }
  return Date.now() - token.fetchedAt > 50 * 60 * 1000;
}

function normalizeIds(ids?: string[]): string[] {
  return Array.from(new Set((ids ?? []).filter(Boolean))).sort();
}

function tokenRequestKey(params?: TokenRequestParams): string {
  if (!params) return "default";
  return JSON.stringify({
    conversationIds: normalizeIds(params.conversationIds),
    channelIds: normalizeIds(params.channelIds),
    presenceUserIds: normalizeIds(params.presenceUserIds),
  });
}

function recordTokenFailure(key: string) {
  const current = tokenBackoff.get(key);
  const delayMs = Math.min(current ? current.delayMs * 2 : 5_000, 60_000);
  tokenBackoff.set(key, { delayMs, until: Date.now() + delayMs });
}

async function getMomentoToken(params?: TokenRequestParams): Promise<MomentoToken> {
  const key = tokenRequestKey(params);
  const cached = tokenCache.get(key) ?? null;
  if (!shouldRefresh(cached)) return cached!;
  const backoff = tokenBackoff.get(key);
  if (backoff && Date.now() < backoff.until) {
    throw new Error("Momento token temporarily unavailable; retrying soon");
  }
  const existingPromise = tokenPromises.get(key);
  if (existingPromise) return existingPromise;

  const promise = (async () => {
    let data: RealtimeTokenResponse;
    if (params) {
      data = await apiClient.post<RealtimeTokenResponse>("/api/chat/token", params);
    } else {
      data = await apiClient.get<RealtimeTokenResponse>("/api/chat/token");
    }
    const next: MomentoToken = { ...data, fetchedAt: Date.now() };
    tokenCache.set(key, next);
    tokenBackoff.delete(key);
    return next;
  })();
  tokenPromises.set(key, promise);
  try {
    return await promise;
  } catch (err) {
    recordTokenFailure(key);
    throw err;
  } finally {
    tokenPromises.delete(key);
  }
}

export interface MomentoSubscription {
  unsubscribe: () => void;
}

function getTopicPrefix(token: MomentoToken): string {
  if (token.topicPrefix) return token.topicPrefix;
  const userTopic = token.topics.find((topic) => topic.includes(".users."));
  if (userTopic) return userTopic.slice(0, userTopic.indexOf(".users."));
  return "slumbr";
}

function getOrCreateTopicClient(token: MomentoToken): TopicClient {
  const cached = topicClientCache.get(token.apiKey);
  if (cached) return cached;
  const client = new TopicClient({
    // Disposable tokens encode their endpoint internally — pass just the
    // token string and Momento extracts the right control/data endpoints.
    credentialProvider: CredentialProvider.fromString(token.apiKey),
    configuration: Configurations.Browser.latest(),
  });
  if (topicClientCache.size >= TOPIC_CLIENT_CACHE_MAX) {
    // Live subscriptions keep their own reference; this only drops reuse.
    const oldest = topicClientCache.keys().next().value;
    if (oldest !== undefined) topicClientCache.delete(oldest);
  }
  topicClientCache.set(token.apiKey, client);
  return client;
}

function getOrCreateCacheClient(token: MomentoToken): CacheClient {
  const cacheToken = token.cacheApiKey ?? token.apiKey;
  if (cacheClientCache && cacheClientCache.token === cacheToken) {
    return cacheClientCache.client;
  }
  const client = new CacheClient({
    // Disposable tokens encode their endpoint internally — pass just the
    // token string and Momento extracts the right control/data endpoints.
    credentialProvider: CredentialProvider.fromString(cacheToken),
    configuration: Configurations.Browser.latest(),
    defaultTtlSeconds: 60,
  });
  cacheClientCache = { token: cacheToken, client };
  return client;
}

export async function getDirectMomentoTopicClient(): Promise<{
  client: TopicClient;
  cacheName: string;
  token: MomentoToken;
}> {
  const token = await getMomentoToken();
  return {
    client: getOrCreateTopicClient(token),
    cacheName: token.cacheName,
    token,
  };
}

export async function getDirectMomentoCacheClient(): Promise<{
  client: CacheClient;
  cacheName: string;
  token: MomentoToken;
}> {
  const token = await getMomentoToken();
  return {
    client: getOrCreateCacheClient(token),
    cacheName: token.cacheName,
    token,
  };
}

/**
 * Direct-from-browser publish via a scoped Momento token. Pass `scope` when
 * the topic needs a resource grant (e.g. `typing.{conversationId}` needs
 * `conversationIds`) — the default app-level token can't publish there.
 *
 * Throws on a Momento error response: the SDK returns errors (e.g.
 * PERMISSION_DENIED) as values rather than throwing, which previously let
 * failed publishes vanish silently.
 */
export async function publishToTopic(
  topic: string,
  payload: unknown,
  scope?: TokenRequestParams
): Promise<void> {
  const token = await getMomentoToken(scope);
  const client = getOrCreateTopicClient(token);
  const result = await client.publish(token.cacheName, topic, JSON.stringify(payload));
  if (result instanceof TopicPublish.Error) {
    throw new Error(`Momento publish to ${topic} failed: ${result.errorCode()} ${result.message()}`);
  }
}

/** Topic prefix for a scoped token (falls back to the app-level token). */
export async function getMomentoTopicPrefix(scope?: TokenRequestParams): Promise<string> {
  return getTopicPrefix(await getMomentoToken(scope));
}

/**
 * Self-healing topic subscription.
 *
 * Disposable Momento tokens expire (~1h). Without recovery, a token expiry or
 * transient stream error kills the subscription silently and the tab stops
 * receiving realtime events until a full reload. This wrapper:
 *   - resubscribes with a fresh token after any subscription error
 *     (break-before-make, capped exponential backoff),
 *   - proactively rotates the subscription shortly before token expiry
 *     (make-before-break so no event gap; consumers dedupe by message id),
 *   - fast-retries when the browser reports connectivity is back.
 */
function subscribeToMomentoTopics(
  getTopics: (token: MomentoToken) => string[],
  onMessage: (topic: string, item: TopicItem) => void,
  onError?: (err: Error) => void,
  params?: TokenRequestParams,
  onRecovered?: () => void
): MomentoSubscription {
  let disposed = false;
  let generation = 0;
  let recovering = false;
  let activeSubs: Array<{ unsubscribe: () => void }> = [];
  let restartTimer: ReturnType<typeof setTimeout> | null = null;
  let rotateTimer: ReturnType<typeof setTimeout> | null = null;
  let restartDelayMs = 2_000;

  const clearTimers = () => {
    if (restartTimer) {
      clearTimeout(restartTimer);
      restartTimer = null;
    }
    if (rotateTimer) {
      clearTimeout(rotateTimer);
      rotateTimer = null;
    }
  };

  const teardown = () => {
    for (const subscription of activeSubs) {
      try {
        subscription.unsubscribe();
      } catch {
        /* already dead */
      }
    }
    activeSubs = [];
  };

  const scheduleRestart = (reason: string) => {
    if (disposed || restartTimer) return;
    const delay = restartDelayMs;
    restartDelayMs = Math.min(restartDelayMs * 2, 60_000);
    console.warn(`[Momento] resubscribing in ${delay}ms (${reason})`);
    recovering = true; // events may be missed until we're back — notify on recovery
    restartTimer = setTimeout(() => {
      restartTimer = null;
      void start({ makeBeforeBreak: false });
    }, delay);
  };

  const onSubscriptionError = (myGeneration: number, err: unknown) => {
    if (disposed || myGeneration !== generation) return;
    console.error("[Momento] subscription error:", String(err));
    onError?.(err instanceof Error ? err : new Error(String(err)));
    // The token may be the problem (expired / revoked) — drop the cache so
    // the restart mints a fresh one.
    invalidateMomentoToken();
    generation += 1; // ignore further errors from this generation
    teardown();
    scheduleRestart("stream error");
  };

  const scheduleProactiveRotation = (token: MomentoToken) => {
    if (disposed || !token.expiresAt) return;
    // Rotate 4 minutes before expiry (getMomentoToken refreshes within 5).
    const rotateAt = token.expiresAt - 4 * 60 * 1000;
    const delay = Math.max(rotateAt - Date.now(), 30_000);
    if (rotateTimer) clearTimeout(rotateTimer);
    rotateTimer = setTimeout(() => {
      rotateTimer = null;
      void start({ makeBeforeBreak: true });
    }, delay);
  };

  const start = async (opts: { makeBeforeBreak: boolean }): Promise<void> => {
    if (disposed) return;
    const myGeneration = ++generation;
    const previousSubs = activeSubs;
    if (!opts.makeBeforeBreak) {
      teardown();
    } else {
      activeSubs = [];
    }
    try {
      const token = await getMomentoToken(params);
      const { cacheName } = token;
      const topics = Array.from(new Set(getTopics(token).filter(Boolean)));

      if (disposed || myGeneration !== generation) return;
      if (topics.length === 0) return;

      const topicClient = getOrCreateTopicClient(token);

      const results = await Promise.all(
        topics.map((topic) =>
          topicClient.subscribe(cacheName, topic, {
            onItem: (item) => {
              if (!disposed && myGeneration === generation) onMessage(topic, item);
            },
            onError: (err) => {
              onSubscriptionError(myGeneration, err);
            },
          })
        )
      );

      const nextSubs: Array<{ unsubscribe: () => void }> = [];
      let failures = 0;
      for (const result of results) {
        if (result instanceof TopicSubscribe.Error) {
          console.error("[Momento] subscribe failed:", result.errorCode(), result.toString());
          failures += 1;
          continue;
        }
        const subscription = result as { unsubscribe?: () => void };
        if (typeof subscription.unsubscribe === "function") {
          nextSubs.push({ unsubscribe: () => subscription.unsubscribe?.() });
        }
      }

      if (disposed || myGeneration !== generation) {
        for (const subscription of nextSubs) subscription.unsubscribe();
        return;
      }

      activeSubs = nextSubs;
      // New generation is live — retire the old one (make-before-break path).
      for (const subscription of previousSubs) {
        try {
          subscription.unsubscribe();
        } catch {
          /* already dead */
        }
      }

      if (failures > 0 && nextSubs.length === 0) {
        scheduleRestart("all topics failed to subscribe");
        return;
      }
      restartDelayMs = 2_000; // healthy — reset backoff
      if (recovering) {
        recovering = false;
        onRecovered?.();
      }
      scheduleProactiveRotation(token);
    } catch (err) {
      console.error("[Momento] failed to subscribe:", err);
      if (!disposed && myGeneration === generation) {
        onError?.(err instanceof Error ? err : new Error(String(err)));
        scheduleRestart("token fetch failed");
      }
    }
  };

  const onOnline = () => {
    if (disposed) return;
    // Connectivity is back — don't wait out a long backoff.
    if (restartTimer) {
      clearTimeout(restartTimer);
      restartTimer = null;
      restartDelayMs = 2_000;
      void start({ makeBeforeBreak: false });
    }
  };
  if (typeof window !== "undefined") {
    window.addEventListener("online", onOnline);
  }

  void start({ makeBeforeBreak: false });

  return {
    unsubscribe: () => {
      disposed = true;
      clearTimers();
      teardown();
      if (typeof window !== "undefined") {
        window.removeEventListener("online", onOnline);
      }
    },
  };
}

export function subscribeToRealtime(
  onMessage: (topic: string, item: TopicItem) => void,
  onError?: (err: Error) => void,
  onRecovered?: () => void
): MomentoSubscription {
  return subscribeToMomentoTopics(
    (token) => {
      const grant = token.grant;
      if (grant) {
        // Subscribe to ONLY the user-fanout topic. Per-conversation topics are
        // subscribed on-demand by the conversation view to keep the active
        // subscription count proportional to the visible UI.
        return [grant.user, grant.notifications];
      }
      return token.topics;
    },
    onMessage,
    onError,
    undefined,
    onRecovered
  );
}

/**
 * Subscribe to a single Momento topic, scoping the token request via the
 * optional params so the disposable token actually carries permission
 * for that topic. Returns an item-shaped subscription (use `valueString`
 * on the item to read the payload).
 */
export async function subscribeToMomentoTopic(
  topicSuffix: string,
  onItem: (item: TopicItem) => void,
  scope?: TokenRequestParams
): Promise<MomentoSubscription> {
  return new Promise<MomentoSubscription>((resolve) => {
    const sub = subscribeToMomentoTopics(
      (token) => [`${getTopicPrefix(token)}.${topicSuffix}`],
      (_topic, item) => onItem(item),
      undefined,
      scope
    );
    resolve(sub);
  });
}

export function subscribeToConversationTopics(
  conversationIds: string[],
  onMessage: (topic: string, item: TopicItem) => void,
  onError?: (err: Error) => void
): MomentoSubscription {
  const ids = Array.from(new Set(conversationIds.filter(Boolean)));
  if (ids.length === 0) return { unsubscribe: () => {} };
  return subscribeToMomentoTopics(
    (token) => ids.map((id) => `${getTopicPrefix(token)}.chat.conversation.${id}`),
    onMessage,
    onError,
    { conversationIds: ids }
  );
}

export function subscribeToPresence(
  userIds: string[],
  onStatus: (status: PresenceStatus) => void,
  onError?: (err: Error) => void
): MomentoSubscription {
  const ids = Array.from(new Set(userIds.filter(Boolean)));
  if (ids.length === 0) return { unsubscribe: () => {} };

  return subscribeToMomentoTopics(
    (token) => ids.map((id) => `${getTopicPrefix(token)}.presence.${id}`),
    (_topic, item) => {
      let event: { type?: string; status?: PresenceStatus };
      try {
        event = JSON.parse(item.valueString()) as { type?: string; status?: PresenceStatus };
      } catch {
        return;
      }
      if (event.type === "presence.updated" && event.status?.userId) {
        onStatus(event.status);
      }
    },
    onError,
    { presenceUserIds: ids }
  );
}

export function invalidateMomentoToken() {
  tokenCache.clear();
  tokenPromises.clear();
  tokenBackoff.clear();
  topicClientCache.clear();
  cacheClientCache = null;
}
